import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  BarChart3,
  Search,
  RefreshCw,
  Eye,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminFetch } from '../lib/api';
import { CHAPTER_IDS } from '@shared/chapters';

const api = async (path: string, opts: any = {}) => {
  const resp = await adminFetch(`/api/admin/image-review${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  return resp;
};

type Tab = 'dashboard' | 'backlog' | 'queue';

export const ImageReviewPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('dashboard');

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">🔍 Bildprüfung (IQC)</h1>
      </div>
      <div className="flex gap-2 mb-6">
        {(['dashboard', 'backlog', 'queue'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`btn ${tab === t ? 'btn-primary' : 'btn-ghost'}`}
            style={{ padding: '8px 16px', fontSize: 13 }}
          >
            {t === 'dashboard'
              ? '📊 Dashboard'
              : t === 'backlog'
                ? '🔄 Backlog-Scan'
                : '📋 Prüf-Queue'}
          </button>
        ))}
      </div>
      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'backlog' && <BacklogTab />}
      {tab === 'queue' && <QueueTab />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Dashboard Tab
// ═══════════════════════════════════════════════════════════════════
const DashboardTab: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api('/dashboard');
        setStats(await resp.json());
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="flex justify-center" style={{ padding: 40 }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  if (!stats) return <p className="text-muted">Keine Daten</p>;

  const exportCSV = async () => {
    const resp = await api('/export');
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `image-reviews.csv`;
    a.click();
  };

  return (
    <div>
      {/* KPIs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <KPI label="Geprüft" value={stats.totalReviews} color="#3B82F6" />
        <KPI label="Freigegeben" value={stats.approved} color="#22C55E" />
        <KPI label="Überarbeitung" value={stats.rework} color="#F59E0B" />
        <KPI label="Abgelehnt" value={stats.rejected} color="#EF4444" />
        <KPI label="Freigabequote" value={`${stats.approvalRate}%`} color="#8B5CF6" />
        <KPI label="Ungeprüft" value={stats.questionsUnreviewed} color="#6B7280" />
        <KPI label="Geschätzte Kosten" value={`€${stats.estimatedCost}`} color="#E03705" />
      </div>

      {/* Top Errors */}
      {stats.topErrors?.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <AlertTriangle size={16} /> Häufigste Fehler (Memory)
          </h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {stats.topErrors.map((e: any, i: number) => (
              <div key={i} className="flex items-center gap-3" style={{ fontSize: 13 }}>
                <span
                  style={{
                    color:
                      e.error_class === 'kritisch'
                        ? '#EF4444'
                        : e.error_class === 'mittel'
                          ? '#F59E0B'
                          : '#6B7280',
                    fontWeight: 700,
                    minWidth: 24,
                  }}
                >
                  {e.occurrence_count}x
                </span>
                <span className="text-muted" style={{ minWidth: 80 }}>
                  Kap. {e.chapter_id}
                </span>
                <span>{e.error_type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chapter Stats Table */}
      {stats.chapterStats?.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <BarChart3 size={16} /> Kapitel-Übersicht
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                  <th style={{ padding: '8px 6px', fontWeight: 700 }}>Kapitel</th>
                  <th style={{ padding: '8px 6px', fontWeight: 700, textAlign: 'center' }}>
                    Bilder
                  </th>
                  <th style={{ padding: '8px 6px', fontWeight: 700, textAlign: 'center' }}>
                    Geprüft
                  </th>
                  <th
                    style={{
                      padding: '8px 6px',
                      fontWeight: 700,
                      textAlign: 'center',
                      color: '#22C55E',
                    }}
                  >
                    ✅
                  </th>
                  <th
                    style={{
                      padding: '8px 6px',
                      fontWeight: 700,
                      textAlign: 'center',
                      color: '#F59E0B',
                    }}
                  >
                    ⚠️
                  </th>
                  <th
                    style={{
                      padding: '8px 6px',
                      fontWeight: 700,
                      textAlign: 'center',
                      color: '#EF4444',
                    }}
                  >
                    ❌
                  </th>
                  <th style={{ padding: '8px 6px', fontWeight: 700, minWidth: 120 }}>
                    Fortschritt
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.chapterStats.map((ch: any) => {
                  const pct = ch.withImage > 0 ? Math.round((ch.reviewed / ch.withImage) * 100) : 0;
                  return (
                    <tr key={ch.chapterId} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px' }}>Kap. {ch.chapterId}</td>
                      <td style={{ padding: '6px', textAlign: 'center' }}>{ch.withImage}</td>
                      <td style={{ padding: '6px', textAlign: 'center', fontWeight: 700 }}>
                        {ch.reviewed}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'center', color: '#22C55E' }}>
                        {ch.approved}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'center', color: '#F59E0B' }}>
                        {ch.rework}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'center', color: '#EF4444' }}>
                        {ch.rejected}
                      </td>
                      <td style={{ padding: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div
                            style={{
                              flex: 1,
                              background: 'var(--bg-secondary)',
                              borderRadius: 4,
                              height: 8,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${pct}%`,
                                height: '100%',
                                background: pct === 100 ? '#22C55E' : '#3B82F6',
                                borderRadius: 4,
                                transition: 'width 0.3s',
                              }}
                            />
                          </div>
                          <span className="text-muted" style={{ fontSize: 11, minWidth: 30 }}>
                            {pct}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <button
        className="btn btn-ghost flex items-center gap-2"
        onClick={exportCSV}
        style={{ fontSize: 13 }}
      >
        <Download size={14} /> CSV Export
      </button>
    </div>
  );
};

const KPI: React.FC<{ label: string; value: string | number; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div className="card" style={{ padding: '14px 16px', textAlign: 'center' }}>
    <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
    <div className="text-xs text-muted" style={{ marginTop: 4 }}>
      {label}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// Backlog Tab
// ═══════════════════════════════════════════════════════════════════
const VERDICT_COLORS: Record<string, string> = {
  approved: '#22C55E',
  rework: '#F59E0B',
  rejected: '#EF4444',
};
const VERDICT_LABELS: Record<string, string> = {
  approved: '✅ Freigabe',
  rework: '⚠️ Überarbeitung',
  rejected: '❌ Abgelehnt',
};

const BacklogTab: React.FC = () => {
  const [status, setStatus] = useState<any>(null);
  const [starting, setStarting] = useState(false);
  const [limit, setLimit] = useState(20);
  const [chapterId, setChapterId] = useState<number | null>(null);

  const pollStatus = useCallback(async () => {
    try {
      const resp = await api('/backlog/status');
      setStatus(await resp.json());
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  useEffect(() => {
    if (!status?.running) return;
    const i = setInterval(pollStatus, 3000);
    return () => clearInterval(i);
  }, [status?.running, pollStatus]);

  const startScan = async () => {
    setStarting(true);
    try {
      await api('/backlog', { method: 'POST', body: JSON.stringify({ limit, chapterId }) });
      setTimeout(pollStatus, 1000);
    } catch (e) {
      console.error(e);
    } finally {
      setStarting(false);
    }
  };

  const progress = status?.total ? Math.round((status.completed / status.total) * 100) : 0;
  const selectStyle = {
    fontSize: 13,
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
  };

  return (
    <div>
      {/* Start scan */}
      {!status?.running && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <Search size={16} /> Bestehende Bilder prüfen
          </h3>
          <p className="text-xs text-muted" style={{ marginBottom: 12 }}>
            Prüft Fragen mit Bildern, die noch nicht geprüft wurden. Rate-Limit: 1 Bild alle 10
            Sekunden.
          </p>
          <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
            <label className="text-xs text-muted">Kapitel:</label>
            <select
              value={chapterId ?? ''}
              onChange={(e) => setChapterId(e.target.value ? Number(e.target.value) : null)}
              style={selectStyle}
            >
              <option value="">Alle Kapitel</option>
              {CHAPTER_IDS.map((ch) => (
                <option key={ch} value={ch}>
                  Kapitel {ch}
                </option>
              ))}
            </select>
            <label className="text-xs text-muted">Anzahl:</label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              style={selectStyle}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button
              className="btn btn-primary flex items-center gap-2"
              onClick={startScan}
              disabled={starting}
              style={{ fontSize: 13 }}
            >
              {starting ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Starte...
                </>
              ) : (
                <>
                  <RefreshCw size={14} /> Scan starten
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Progress + live preview */}
      {status?.running && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            Scan läuft...{status.chapterId ? ` (Kapitel ${status.chapterId})` : ''}
          </h3>
          <div
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: 8,
              height: 24,
              overflow: 'hidden',
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #3B82F6, #8B5CF6)',
                transition: 'width 0.5s',
                borderRadius: 8,
              }}
            />
          </div>
          <div className="flex gap-4 text-xs text-muted" style={{ marginBottom: 16 }}>
            <span>
              {status.completed} / {status.total} geprüft ({progress}%)
            </span>
            <span style={{ color: '#22C55E' }}>✅ {status.approved}</span>
            <span style={{ color: '#F59E0B' }}>⚠️ {status.rework}</span>
            <span style={{ color: '#EF4444' }}>❌ {status.rejected}</span>
          </div>

          {/* Current question being reviewed */}
          {status.currentQuestion && (
            <div
              style={{
                padding: 12,
                background: 'var(--bg-secondary)',
                borderRadius: 12,
                marginBottom: 12,
                borderLeft: '3px solid #3B82F6',
              }}
            >
              <div className="text-xs text-muted" style={{ marginBottom: 6 }}>
                🔍 Wird gerade geprüft (Kap. {status.currentQuestion.chapterId}):
              </div>
              <div className="flex gap-3" style={{ alignItems: 'center' }}>
                <img
                  src={status.currentQuestion.imageUrl}
                  alt="Current"
                  style={{
                    width: 64,
                    height: 48,
                    objectFit: 'cover',
                    borderRadius: 6,
                    flexShrink: 0,
                  }}
                />
                <div className="text-xs" style={{ lineHeight: 1.4 }}>
                  {status.currentQuestion.text}
                </div>
              </div>
            </div>
          )}

          {/* Recent results */}
          {status.lastResults?.length > 0 && (
            <div>
              <div className="text-xs text-muted font-bold" style={{ marginBottom: 6 }}>
                Letzte Ergebnisse:
              </div>
              <div style={{ display: 'grid', gap: 6 }}>
                {[...status.lastResults].reverse().map((r: any, i: number) => (
                  <div
                    key={i}
                    className="flex items-center gap-3"
                    style={{
                      fontSize: 12,
                      padding: '6px 8px',
                      background: 'var(--bg-secondary)',
                      borderRadius: 8,
                    }}
                  >
                    <img
                      src={r.imageUrl}
                      alt=""
                      style={{
                        width: 36,
                        height: 28,
                        objectFit: 'cover',
                        borderRadius: 4,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        color: VERDICT_COLORS[r.verdict],
                        fontWeight: 700,
                        minWidth: 20,
                        fontSize: 11,
                      }}
                    >
                      {r.score}
                    </span>
                    <span style={{ color: VERDICT_COLORS[r.verdict], fontSize: 11, minWidth: 90 }}>
                      {VERDICT_LABELS[r.verdict]}
                    </span>
                    <span className="text-muted" style={{ fontSize: 11, minWidth: 40 }}>
                      Kap.{r.chapterId}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results (after scan) */}
      {status && !status.running && status.total > 0 && (
        <div className="card" style={{ padding: 20 }}>
          <h3 className="text-sm font-bold mb-3">
            Scan-Ergebnis ({status.total} Bilder
            {status.chapterId ? ` • Kapitel ${status.chapterId}` : ''})
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                textAlign: 'center',
                padding: 12,
                background: 'rgba(34,197,94,0.1)',
                borderRadius: 12,
              }}
            >
              <CheckCircle size={20} style={{ color: '#22C55E', margin: '0 auto 4px' }} />
              <div style={{ fontSize: 20, fontWeight: 800, color: '#22C55E' }}>
                {status.approved}
              </div>
              <div className="text-xs text-muted">Freigegeben</div>
            </div>
            <div
              style={{
                textAlign: 'center',
                padding: 12,
                background: 'rgba(245,158,11,0.1)',
                borderRadius: 12,
              }}
            >
              <AlertTriangle size={20} style={{ color: '#F59E0B', margin: '0 auto 4px' }} />
              <div style={{ fontSize: 20, fontWeight: 800, color: '#F59E0B' }}>{status.rework}</div>
              <div className="text-xs text-muted">Überarbeitung</div>
            </div>
            <div
              style={{
                textAlign: 'center',
                padding: 12,
                background: 'rgba(239,68,68,0.1)',
                borderRadius: 12,
              }}
            >
              <XCircle size={20} style={{ color: '#EF4444', margin: '0 auto 4px' }} />
              <div style={{ fontSize: 20, fontWeight: 800, color: '#EF4444' }}>
                {status.rejected}
              </div>
              <div className="text-xs text-muted">Abgelehnt</div>
            </div>
          </div>

          {/* Last results from completed scan */}
          {status.lastResults?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div className="text-xs text-muted font-bold" style={{ marginBottom: 6 }}>
                Ergebnisse:
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                {status.lastResults.map((r: any, i: number) => (
                  <div
                    key={i}
                    className="flex items-center gap-3"
                    style={{
                      fontSize: 12,
                      padding: '4px 8px',
                      background: 'var(--bg-secondary)',
                      borderRadius: 6,
                    }}
                  >
                    <img
                      src={r.imageUrl}
                      alt=""
                      style={{
                        width: 32,
                        height: 24,
                        objectFit: 'cover',
                        borderRadius: 4,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{ color: VERDICT_COLORS[r.verdict], fontWeight: 700, fontSize: 11 }}
                    >
                      {r.score}
                    </span>
                    <span style={{ color: VERDICT_COLORS[r.verdict], fontSize: 11, minWidth: 80 }}>
                      {VERDICT_LABELS[r.verdict]}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {status.errors?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <p className="text-xs font-bold text-muted mb-1">Fehler:</p>
              {status.errors.slice(0, 5).map((e: string, i: number) => (
                <p key={i} className="text-xs" style={{ color: '#EF4444' }}>
                  {e}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Queue Tab
// ═══════════════════════════════════════════════════════════════════
const QueueTab: React.FC = () => {
  const [queue, setQueue] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string>('');
  const [regenerating, setRegenerating] = useState<Record<string, boolean>>({});
  const [comparisons, setComparisons] = useState<
    Record<string, { oldUrl: string; newUrl: string }>
  >({});
  const [editedPrompts, setEditedPrompts] = useState<Record<string, string>>({});
  const [lightboxUrl, setLightboxUrl] = useState<string>('');

  useEffect(() => {
    loadQueue();
  }, []);

  const loadQueue = async () => {
    setLoading(true);
    try {
      const resp = await api('/queue');
      const data = await resp.json();
      setQueue(data.queue || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const approveItem = async (id: string) => {
    await api(`/${id}/approve`, { method: 'PUT' });
    setQueue((prev) => prev.filter((q) => q.id !== id));
  };

  const regenerateImage = async (reviewId: string) => {
    setRegenerating((prev) => ({ ...prev, [reviewId]: true }));
    try {
      const customPrompt = editedPrompts[reviewId];
      const resp = await api(`/regenerate/${reviewId}`, {
        method: 'POST',
        body: JSON.stringify({ customPrompt: customPrompt || undefined }),
      });
      const data = await resp.json();
      if (data.success) {
        setComparisons((prev) => ({
          ...prev,
          [reviewId]: { oldUrl: data.oldImageUrl, newUrl: data.newImageUrl },
        }));
        setQueue((prev) =>
          prev.map((q) => (q.id === reviewId ? { ...q, image_url: data.newImageUrl } : q))
        );
      } else {
        alert(`Fehler: ${data.error || 'Unbekannter Fehler'}`);
      }
    } catch (e: any) {
      alert(`Fehler: ${e.message}`);
    } finally {
      setRegenerating((prev) => ({ ...prev, [reviewId]: false }));
    }
  };

  if (loading)
    return (
      <div className="flex justify-center" style={{ padding: 40 }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );

  if (queue.length === 0) {
    return (
      <div className="text-center" style={{ padding: 40 }}>
        <CheckCircle size={48} style={{ color: '#22C55E', margin: '0 auto 12px', opacity: 0.5 }} />
        <p className="font-bold">Keine Bilder in der Queue</p>
        <p className="text-xs text-muted" style={{ marginTop: 4 }}>
          Alle geprüften Bilder sind freigegeben oder starte einen Backlog-Scan.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-muted mb-4">{queue.length} Bilder zur manuellen Prüfung</p>

      {/* Fullscreen Lightbox */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl('')}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: 20,
          }}
        >
          <img
            src={lightboxUrl}
            alt="Vollbild"
            style={{ maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 12 }}
          />
          <button
            onClick={() => setLightboxUrl('')}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            ✕ Schließen
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {queue.map((item) => {
          const comparison = comparisons[item.id];
          const isRegenerating = regenerating[item.id];
          const promptText = editedPrompts[item.id] ?? item.optimized_prompt ?? '';

          return (
            <div key={item.id} className="card" style={{ padding: 16 }}>
              {/* Header: Question text + badge */}
              <div style={{ marginBottom: 12 }}>
                <div className="text-sm font-bold" style={{ marginBottom: 6 }}>
                  {item.question?.question_text_de || item.question_id}
                </div>
                <div className="flex gap-2 items-center">
                  <span
                    style={{
                      fontSize: 11,
                      padding: '2px 10px',
                      borderRadius: 6,
                      fontWeight: 700,
                      background:
                        item.verdict === 'rejected'
                          ? 'rgba(239,68,68,0.15)'
                          : 'rgba(245,158,11,0.15)',
                      color: item.verdict === 'rejected' ? '#EF4444' : '#F59E0B',
                    }}
                  >
                    {item.verdict === 'rejected' ? 'ABGELEHNT' : 'ÜBERARBEITUNG'} — Score:{' '}
                    {item.score}
                  </span>
                  <span className="text-xs text-muted">Kap. {item.question?.chapter_id}</span>
                  <span className="text-xs text-muted">{item.error_class}</span>
                </div>
              </div>

              {/* Image: before/after comparison or large preview with click-to-fullscreen */}
              {comparison ? (
                <div style={{ marginBottom: 12 }}>
                  <div className="text-xs font-bold text-muted" style={{ marginBottom: 8 }}>
                    Vorher / Nachher Vergleich:
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div
                        className="text-xs text-muted"
                        style={{ marginBottom: 4, textAlign: 'center' }}
                      >
                        ❌ Vorher (alt)
                      </div>
                      <img
                        src={comparison.oldUrl}
                        alt="Old"
                        onClick={() => setLightboxUrl(comparison.oldUrl)}
                        style={{
                          width: '100%',
                          maxHeight: 400,
                          objectFit: 'contain',
                          borderRadius: 10,
                          border: '2px solid rgba(239,68,68,0.3)',
                          cursor: 'zoom-in',
                        }}
                      />
                    </div>
                    <div>
                      <div
                        className="text-xs text-muted"
                        style={{ marginBottom: 4, textAlign: 'center' }}
                      >
                        ✅ Nachher (neu)
                      </div>
                      <img
                        src={comparison.newUrl}
                        alt="New"
                        onClick={() => setLightboxUrl(comparison.newUrl)}
                        style={{
                          width: '100%',
                          maxHeight: 400,
                          objectFit: 'contain',
                          borderRadius: 10,
                          border: '2px solid rgba(34,197,94,0.3)',
                          cursor: 'zoom-in',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 12 }}>
                  <img
                    src={item.image_url}
                    alt="Review"
                    onClick={() => setLightboxUrl(item.image_url)}
                    style={{
                      width: '100%',
                      maxHeight: 500,
                      objectFit: 'contain',
                      borderRadius: 10,
                      border: '1px solid var(--border)',
                      cursor: 'zoom-in',
                    }}
                  />
                  <div className="text-xs text-muted" style={{ marginTop: 4, textAlign: 'center' }}>
                    Klicke zum Vergrößern
                  </div>
                </div>
              )}

              {/* Problems */}
              {item.problems?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div className="text-xs font-bold text-muted" style={{ marginBottom: 4 }}>
                    Probleme:
                  </div>
                  {item.problems.map((p: string, i: number) => (
                    <div key={i} className="text-xs" style={{ color: '#EF4444', lineHeight: 1.5 }}>
                      • {p}
                    </div>
                  ))}
                </div>
              )}

              {/* Editable prompt */}
              {item.optimized_prompt && (
                <div style={{ marginBottom: 12 }}>
                  <div className="text-xs font-bold text-muted" style={{ marginBottom: 4 }}>
                    Prompt (editierbar — anpassen und „Neu generieren"):
                  </div>
                  <textarea
                    value={promptText}
                    onChange={(e) =>
                      setEditedPrompts((prev) => ({ ...prev, [item.id]: e.target.value }))
                    }
                    style={{
                      width: '100%',
                      minHeight: 100,
                      padding: 10,
                      fontSize: 12,
                      lineHeight: 1.6,
                      borderRadius: 10,
                      border: '1px solid var(--border)',
                      background: 'var(--bg-secondary)',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                <button
                  className="btn btn-ghost btn-sm flex items-center gap-1"
                  onClick={() => approveItem(item.id)}
                  style={{
                    fontSize: 12,
                    color: '#22C55E',
                    border: '1px solid rgba(34,197,94,0.3)',
                    borderRadius: 8,
                  }}
                >
                  <CheckCircle size={14} /> Freigeben
                </button>
                {item.optimized_prompt && !isRegenerating && (
                  <button
                    className="btn btn-ghost btn-sm flex items-center gap-1"
                    onClick={() => regenerateImage(item.id)}
                    style={{
                      fontSize: 12,
                      color: '#3B82F6',
                      border: '1px solid rgba(59,130,246,0.3)',
                      borderRadius: 8,
                    }}
                  >
                    <RefreshCw size={14} /> Neu generieren
                  </button>
                )}
                {isRegenerating && (
                  <span
                    className="flex items-center gap-2 text-xs text-muted"
                    style={{ padding: '4px 8px' }}
                  >
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Bild wird
                    generiert...
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
